// Patient roster page (/patients) — clinical-only (doctor/admin/superadmin).
//
// Lists patients_with_ehrid alphabetically, a selectable number per page
// (10 by default, up to 1000 — see PAGE_SIZE_OPTIONS). By default the WHOLE
// roster is listed; the "Only without an appointment" checkbox narrows it to
// patients with no notes-c visit. That is an anti-join PostgREST cannot
// express, so it is evaluated client-side against a live notes-c lookup keyed
// on vita_id (utils/rosterVisitLookup) — NOT against the roster's
// source_record_ids snapshot, which is stale in both directions. Search
// (name, EHR ID or email) always queries the whole roster so anyone can be
// pulled up. Clicking a never-visited patient opens their patient-level chart
// (/patients/:id); a visited patient gets a chooser (Patient chart / Latest
// visit).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@chakra-ui/react';
import { getSupabase } from '../components/supabaseClient';
import { shadowComparePatientList } from '../utils/rdsShadowRead';
import {
  isCutoverEffective, serveRosterFromRds, startRosterFromRds,
  startLatestVisitFromRds, serveLatestVisitFromRds,
} from '../utils/rdsCutover';
import { fetchEffectiveUserRow } from '../utils/effectiveUserRole';
import { canAccessPage, hasClinicalAdminPermissions } from '../config/roles';
import { getPatientPageVisitPathSegment } from '../utils/visitPathUtils';
import LeejoeSchedulesView from '../components/LeejoeSchedulesView';
import RosterTable from '../components/RosterTable';
import { mapRosterRowToPatient } from '../hooks/medstudent/usePatientDocument';
import { useSelectedProviders } from '../hooks/useSelectedProviders';
import ProvidersDropdown from '../components/ProvidersDropdown';
import { fetchVisitCountsByVitaId, annotateVisitState } from '../utils/rosterVisitLookup';

export const PAGE_SIZE = 10;
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 1000];

// Rows fetched per schema before the merge + client-side slice. The page sorts
// across schemas, so it cannot let the database do the paging — it has to hold
// the whole candidate set. Anything past this cap is invisible, which is why
// hitting it is reported to the user (see `truncatedSchemas`) rather than only
// to the console: the largest page size is deliberately no larger than the cap,
// so a full page is exactly the point at which rows start going missing.
export const FETCH_CAP = 1000;

export default function PatientListPage() {
  const navigate = useNavigate();
  const toast = useToast();

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  // Schemas whose fetch came back full — their rosters are larger than the
  // page can see. Surfaced in the UI; a console.warn alone made the missing
  // patients look like they simply weren't there.
  const [truncatedSchemas, setTruncatedSchemas] = useState([]);
  const [allCount, setAllCount] = useState(null);
  const [neverSeenCount, setNeverSeenCount] = useState(null);
  // Opt-IN narrowing: the roster lists everyone by default, and this checkbox
  // filters down to patients with no appointment. (It used to be the inverse —
  // a "Show all patients" box off by default.)
  const [onlyWithoutAppointment, setOnlyWithoutAppointment] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [isSimplifiedView, setIsSimplifiedView] = useState(true);
  const [chooserRow, setChooserRow] = useState(null);
  const searchDebounceRef = useRef(null);
  // Cross-schema providers filter — `selected` (always includes `homeSchema`)
  // drives the fetchPage fan-out below; a single-schema user just gets
  // selected === [homeSchema], reducing every code path here to today's
  // single-schema behavior.
  const { selected, homeSchema } = useSelectedProviders();
  // Guards against a stale {page, search, onlyWithoutAppointment} response
  // resolving after a newer one (e.g. the filter changes while page > 0, racing the
  // setPage(0) reset below) and overwriting the correct rows/count. Same
  // pattern as usePatientDocument's fetchGenRef.
  const fetchGenRef = useRef(0);
  // Phase 3: the in-flight RDS roster queries of the CURRENT generation, so
  // a newer fetch can abort them instead of letting RDS finish (and audit)
  // a 1000-row page nobody will look at.
  const rdsAbortRef = useRef(null);

  // ── Role gate — same pattern as Schedules.checkUserRole, but clinical-only
  // and WITHOUT the admin → /admin-page redirect (admin is a first-class user
  // of this page).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const supabase = await getSupabase();
        const { data: { session } = {}, error: sessionError } = await supabase.auth.getSession();
        if (cancelled) return;
        if (sessionError || !session) {
          navigate('/auth');
          return;
        }
        const { data, error } = await fetchEffectiveUserRow({ email: session.user.email });
        if (cancelled) return;
        if (error) throw error;
        setUserRole(data.role);
        if (hasClinicalAdminPermissions(data.role)) {
          setIsAuthenticated(true);
        } else {
          toast({
            title: 'Access Denied',
            description: 'You do not have permission to access this page.',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          navigate('/forbidden');
        }
      } catch (err) {
        console.error('[PatientListPage] role check failed:', err);
        navigate('/auth');
      }
    })();
    return () => { cancelled = true; };
  }, [navigate, toast]);

  // ── Data fetch ──────────────────────────────────────────────────────────
  // Fans out the same filtered query across every selected provider schema,
  // merges + re-sorts client-side, then slices the current page window.
  // Single-schema users (selected === [homeSchema], the default) exercise
  // exactly one iteration of the loop below with the same filter predicate
  // the old single-query implementation used, so that case is byte-for-byte
  // unchanged (modulo the DB doing the count vs. us counting the merged
  // array — identical as long as a schema has under 1000 matching rows).
  const fetchPage = useCallback(async () => {
    const myGen = ++fetchGenRef.current;
    if (!isAuthenticated) return;
    setIsLoading(true);
    setLoadError(null);
    try {
      const supabase = await getSupabase();
      const schemas = selected.length ? selected : [homeSchema];
      // Test-integration plan Phase 3: is this roster served from RDS right
      // now? Decided ONCE per fetch (not per schema) so a flag flip mid-loop
      // cannot mix sources within one result, and only after the generation
      // counter is taken so the await below is covered by the same stale-
      // response guard as everything else. False unless BOTH this NotesAI has
      // an RDS_API_URL and RDS's own `effective` flag is on; a
      // failed flag lookup fails closed. When false, everything below is
      // byte-for-byte today's Supabase-only behavior.
      const cutoverOn = await isCutoverEffective('patient_list');
      if (fetchGenRef.current !== myGen) return;
      if (rdsAbortRef.current) rdsAbortRef.current.abort();
      const rdsAbort = cutoverOn ? new AbortController() : null;
      rdsAbortRef.current = rdsAbort;
      const merged = [];
      const truncated = [];
      let failedCount = 0;
      for (const schema of schemas) {
        // A newer fetchPage call already superseded this one (e.g. the user
        // kept typing) — stop issuing further per-schema queries and don't
        // toast for a failure that belongs to a discarded generation. The
        // post-loop check below still discards anything already collected.
        if (fetchGenRef.current !== myGen) break;
        try {
          let query = supabase
            .schema(schema)
            .from('patients_with_ehrid')
            .select('*')
            .order('patient_name', { ascending: true, nullsFirst: false })
            .limit(FETCH_CAP);
          // `,` `(` `)` would break out of the or() list and `%` would fight
          // the wildcards we add ourselves, so they're stripped. `@` and `.`
          // are deliberately KEPT — an email is unsearchable without them,
          // and PostgREST only treats the first two dots of a filter as
          // delimiters, so dots inside the value are safe. Hoisted out of the
          // if() so the RDS cutover query below asks for the very same term.
          const q = search.trim().replace(/[%,()]/g, ' ');
          if (search.trim()) {
            // Search always spans the whole roster (ignores the no-visits
            // filter) so anyone can be pulled up. Email matches on any
            // substring, so a bare local part ("jsmith") or a domain
            // ("@yahoo.com") both work.
            query = query.or(
              `patient_name.ilike.%${q}%,ehr_id.ilike.%${q}%,email.ilike.%${q}%`
            );
          }
          // Phase 3: start RDS's roster query alongside Supabase's so the
          // two round trips overlap; it is settled (or discarded) below
          // once the Supabase rows are in hand. Nothing is started when
          // the cutover is off.
          const rdsStarted = cutoverOn
            ? startRosterFromRds(schema, { search: q, limit: FETCH_CAP, signal: rdsAbort.signal })
            : null;
          const { data, error } = await query;
          if (fetchGenRef.current !== myGen) break;
          if (error) throw error;
          let schemaRows = Array.isArray(data) ? data : [];
          if (schemaRows.length >= FETCH_CAP) {
            console.warn(`[PatientListPage] schema "${schema}" hit the ${FETCH_CAP}-row fetch cap; some patients may be missing.`);
            truncated.push(schema);
          }
          if (cutoverOn) {
            // Phase 3 dual-read: the Supabase rows above still ran and stay
            // the parity oracle + fallback, but what the user SEES for this
            // schema is RDS's answer (with RDS's own row-level authorization
            // — rows the viewer is not entitled to are simply absent). RDS
            // failing or timing out resolves to the Supabase rows instead;
            // either way the outcome is reported. See utils/rdsCutover.js.
            const served = await serveRosterFromRds(schema, {
              search: q, supabaseRows: schemaRows, limit: FETCH_CAP, started: rdsStarted,
            });
            if (fetchGenRef.current !== myGen) break;
            schemaRows = served.rows;
            // RDS's own "page was full" signal feeds the same UI as the
            // Supabase cap check above (a schema is listed once at most).
            if (served.truncated && !truncated.includes(schema)) {
              console.warn(`[PatientListPage] schema "${schema}" hit the ${FETCH_CAP}-row cap on RDS; some patients may be missing.`);
              truncated.push(schema);
            }
          }
          // "Has an appointment?" is answered from notes-c, never from the
          // roster's stale source_record_ids snapshot. See rosterVisitLookup.
          const counts = await fetchVisitCountsByVitaId(
            supabase, schema, schemaRows.map((r) => r.vita_id),
          );
          if (fetchGenRef.current !== myGen) break;
          annotateVisitState(schemaRows, counts)
            .forEach((r) => merged.push({ ...r, _schema: schema }));
        } catch (err) {
          if (fetchGenRef.current !== myGen) break;
          failedCount += 1;
          console.error(`[PatientListPage] fetch failed for schema "${schema}":`, err);
          toast({
            title: 'Could not load one provider’s patients',
            description: `The "${schema}" roster failed to load; showing the rest.`,
            status: 'warning',
            duration: 5000,
            isClosable: true,
          });
        }
      }
      if (fetchGenRef.current !== myGen) return;
      if (failedCount === schemas.length) {
        // Every schema failed — same terminal state the old single-query
        // catch branch left behind (error banner, empty page, stale tiles).
        setLoadError('Could not load patients.');
        setRows([]);
        setTotalCount(0);
        setTruncatedSchemas([]);
        return;
      }
      // Rows from different schemas need re-merging by name once combined —
      // each per-schema query is already DB-sorted on its own, but the union
      // isn't. Array.prototype.sort is stable, so a single-schema merge (the
      // default) is a no-op reorder of an already-correctly-sorted list.
      merged.sort((a, b) => {
        const an = a.patient_name;
        const bn = b.patient_name;
        if (an == null && bn == null) return 0;
        if (an == null) return 1; // nulls last
        if (bn == null) return -1;
        return String(an).localeCompare(String(bn));
      });

      // Test-integration plan Phase 1c: fire-and-forget, off by default
      // (REACT_APP_RDS_SHADOW_READ_SCHEDULES). Never awaited -- cannot
      // add latency or affect anything below. Groups the already-merged
      // rows back by the _schema tag applied in the per-schema loop
      // above. Skipped for a Phase 3 cutover fetch: that path already
      // reported a FULL RDS-vs-Supabase comparison per schema, and
      // `merged` would by then hold RDS's rows, so a sample would only
      // compare RDS against itself.
      if (!cutoverOn) {
        const shadowRowsBySchema = {};
        merged.forEach((row) => {
          const sch = row._schema;
          if (!sch) return;
          if (!shadowRowsBySchema[sch]) shadowRowsBySchema[sch] = [];
          shadowRowsBySchema[sch].push(row);
        });
        shadowComparePatientList(shadowRowsBySchema);
      }
      // The "without an appointment" narrowing happens HERE, not in the query:
      // it is an anti-join against notes-c, which PostgREST cannot express.
      // Search deliberately ignores it so anyone can still be pulled up.
      const visible = onlyWithoutAppointment && !search.trim()
        ? merged.filter((r) => !r._hasVisits)
        : merged;

      // `allCount` stays with the exact HEAD-count effect below — it is a plain
      // row count, which the database can do exactly and uncapped. The
      // never-seen count cannot be: it needs the notes-c anti-join, so it is
      // derived from the merged set here and shares ONE definition with the
      // filter above. Skipped while searching, when `merged` is a search result
      // rather than the whole roster and would under-report the tile.
      if (!search.trim()) {
        setNeverSeenCount(merged.filter((r) => !r._hasVisits).length);
      }
      setRows(visible.slice(page * pageSize, page * pageSize + pageSize));
      setTotalCount(visible.length);
      setTruncatedSchemas(truncated);
    } catch (err) {
      console.error('[PatientListPage] fetch failed:', err);
      if (fetchGenRef.current !== myGen) return;
      setLoadError(err?.message || 'Could not load patients.');
      setRows([]);
      setTotalCount(0);
      setTruncatedSchemas([]);
    } finally {
      if (fetchGenRef.current === myGen) setIsLoading(false);
    }
  }, [isAuthenticated, page, pageSize, search, onlyWithoutAppointment, selected, homeSchema, toast]);

  useEffect(() => { fetchPage(); }, [fetchPage]);

  // Reset to page 0 when the query shape changes. Page size counts: page 7 of a
  // 10-row listing does not exist once the listing shows 100 at a time.
  useEffect(() => { setPage(0); }, [search, onlyWithoutAppointment, pageSize]);

  // Whole-roster total for the stat tiles (independent of paging/filter).
  // Schema-aware: sums an exact HEAD count (no rows fetched, so the fetchPage
  // 1000-row/schema cap never applies here) across every selected schema.
  //
  // `allCount` only — it is a plain row count, which the database can do
  // exactly. The never-seen count USED to be computed here as
  // `.eq('source_record_ids','{}')`, but that snapshot is stale in both
  // directions (see utils/rosterVisitLookup), so the tile disagreed with the
  // roster itself: STUDENT reported 1 never-seen patient where 3 had no
  // notes-c row at all. It now comes from fetchPage, which shares ONE
  // definition with the filter.
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    (async () => {
      try {
        const supabase = await getSupabase();
        const schemas = selected.length ? selected : [homeSchema];
        let allSum = 0;
        let succeededAny = false;
        for (const schema of schemas) {
          try {
            const allRes = await supabase
              .schema(schema).from('patients_with_ehrid')
              .select('id', { count: 'exact', head: true });
            if (cancelled) return;
            if (allRes.error) throw allRes.error;
            allSum += allRes.count || 0;
            succeededAny = true;
          } catch (err) {
            // Skip this schema's contribution — the sum still reflects
            // every schema that succeeded rather than aborting entirely.
            console.error(`[PatientListPage] stat-tile count failed for schema "${schema}":`, err);
          }
        }
        if (cancelled || !succeededAny) return;
        setAllCount(allSum);
      } catch (_) {
        // Tiles fall back to the page query's total.
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, selected, homeSchema]);

  // Debounced search box → search state.
  const onSearchInput = (value) => {
    setSearchInput(value);
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => setSearch(value), 300);
  };

  // ── Click behavior ──────────────────────────────────────────────────────
  // A row's own `_schema` (tagged during the fetchPage fan-out) wins so a
  // foreign-provider patient's chart/visit opens against ITS schema, not the
  // viewer's home schema; falls back to homeSchema for rows that predate
  // tagging (e.g. any stale in-memory reference).
  const openPatientChart = useCallback((row) => {
    navigate(`/patients/${row.id}?schema=${encodeURIComponent(row._schema || homeSchema)}`);
  }, [navigate, homeSchema]);

  const openLatestVisit = useCallback(async (row) => {
    const effSchema = row._schema || homeSchema;
    // Phase 4 cutover, site 5 (utils/rdsCutover.js `latest_visit`): when both
    // switches are on, RDS's answer is what drives the navigation while the
    // Supabase read below still runs as the parity oracle and the fallback.
    // Off (the default, and whenever RDS says the flag is not effective) this
    // is byte-for-byte the old behaviour.
    const cutoverOn = await isCutoverEffective('latest_visit');
    try {
      // Started BEFORE the Supabase read so the two round trips overlap.
      const rdsStarted = cutoverOn
        ? startLatestVisitFromRds(effSchema, { vitaId: row.vita_id })
        : null;
      const supabase = await getSupabase();
      const { data, error } = await supabase
        .schema(effSchema)
        .from('notes-c')
        .select('id, date_, patient_name, EHR_ID')
        // By vita_id, not `.in('id', source_record_ids)`: that snapshot misses
        // every visit created since the last patient-table build and points at
        // visits that have since been deleted, so "Latest visit" silently fell
        // through to the patient chart for patients who plainly had one.
        .eq('vita_id', row.vita_id)
        .order('date_', { ascending: false })
        .limit(1);
      if (error) {
        openPatientChart(row);
        return;
      }
      let visitRow = (data && data.length > 0) ? data[0] : null;
      if (rdsStarted) {
        // RDS's answer (its own row-level authorization applied) is what the
        // navigation uses; any RDS failure resolves to the Supabase rows
        // already fetched. Either way the outcome is reported.
        const served = await serveLatestVisitFromRds(effSchema, {
          vitaId: row.vita_id, supabaseRows: data || [], started: rdsStarted,
        });
        visitRow = (served.rows && served.rows.length > 0) ? served.rows[0] : null;
      }
      if (!visitRow) {
        openPatientChart(row);
        return;
      }
      const seg = getPatientPageVisitPathSegment(visitRow);
      const prefix = canAccessPage(userRole, 'clinicPage') ? 'clinic' : 'visit';
      navigate(`/${prefix}/${seg}?date=${visitRow.date_}&schema=${encodeURIComponent(effSchema)}`, {
        state: { rowData: { ...visitRow, _schema: effSchema }, date_: visitRow.date_, name: row.patient_name },
      });
    } catch (err) {
      console.error('[PatientListPage] latest-visit lookup failed:', err);
      openPatientChart(row);
    }
  }, [navigate, openPatientChart, userRole, homeSchema]);

  const onPatientClick = useCallback((row) => {
    if (row._hasVisits) setChooserRow(row);
    else openPatientChart(row);
  }, [openPatientChart]);

  // Rows mapped to the visit shape LeejoeSchedulesView/RosterTable render.
  // mapRosterRowToPatient emits a fixed allowlist, so the fields the click
  // handlers need are re-attached explicitly: the fetch's _schema tag (nav +
  // foreign-row badge), vita_id (the Latest-visit lookup) and the live
  // _hasVisits/_visitCount (the chooser).
  const displayRows = useMemo(
    () => rows.map((r) => ({
      ...mapRosterRowToPatient(r),
      _schema: r._schema,
      vita_id: r.vita_id,
      _hasVisits: !!r._hasVisits,
      _visitCount: r._visitCount || 0,
    })),
    [rows],
  );

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  if (!isAuthenticated) {
    return (
      <div id="clinic-root" className="min-h-[calc(100vh-64px)] bg-slate-50 dark:bg-[#06070d] flex items-center justify-center">
        <p className="text-slate-400 text-sm">Checking access…</p>
      </div>
    );
  }

  const rosterStats = [
    { icon: '👥', value: allCount ?? totalCount, label: 'Patients' },
    { icon: '🆕', value: neverSeenCount ?? (onlyWithoutAppointment && !search ? totalCount : 0), label: 'Never seen' },
    { icon: '📅', value: Math.max(0, (allCount ?? totalCount) - (neverSeenCount ?? 0)), label: 'With visits' },
    { icon: '🔎', value: totalCount, label: 'Matching' },
  ];

  return (
    // Same shell as Schedules.js: #clinic-root scopes the Tailwind utilities,
    // slate background + the soft radial wash, full-width container.
    <div id="clinic-root" className="min-h-[calc(100vh-64px)] bg-slate-50 dark:bg-[#06070d] text-slate-900 dark:text-slate-100">
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0"
        style={{
          backgroundImage:
            'radial-gradient(circle at 18% -10%, rgba(56,189,248,0.10), transparent 45%), radial-gradient(circle at 88% 0%, rgba(244,114,182,0.09), transparent 40%), radial-gradient(circle at 50% 110%, rgba(16,185,129,0.08), transparent 50%)',
        }}
      />
      {/* pb-24 keeps the pagination row clear of the app's floating AI bubble
          (fixed bottom-right) — without it the bubble covers the Next button. */}
      <div className="relative w-full px-4 sm:px-6 lg:px-8 pt-6 pb-24">
        {/* ── Hero — mirrors the Schedules page header ─────────────────── */}
        <header className="mb-3 flex flex-wrap items-end justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <span
                aria-hidden="true"
                className="inline-block w-1 h-3.5 rounded bg-gradient-to-b from-blue-500 to-indigo-500"
              />
              <span className="text-[10px] font-bold tracking-[0.16em] text-slate-500 dark:text-slate-400">
                PATIENT ROSTER
              </span>
              {userRole && (
                <span className="ml-1 rounded-full bg-slate-200/70 dark:bg-slate-800/60 px-2 py-0.5 text-[9px] font-bold tracking-widest text-slate-700 dark:text-slate-300 uppercase">
                  {userRole}
                </span>
              )}
            </div>
            <div className="flex items-baseline gap-3 flex-wrap">
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight leading-tight text-slate-900 dark:text-slate-50">
                Patients
              </h1>
              <span className="text-xs text-slate-500 dark:text-slate-400">
                {onlyWithoutAppointment ? 'Patients without an appointment.' : 'Everyone in the roster.'}
              </span>
            </div>
          </div>
          <div className="flex flex-row items-center gap-2 shrink-0">
            {/* Providers — cross-schema roster filter; self-hides for a
                single-schema user (clinical roles only) */}
            <ProvidersDropdown userRole={userRole} />
            {/* Simplified / full view toggle — same control as Schedules */}
            <button
              type="button"
              onClick={() => setIsSimplifiedView((v) => !v)}
              className={`inline-flex items-center gap-1.5 rounded-md border px-3 h-8 text-sm font-bold tracking-tight shadow-sm transition-colors ${
                isSimplifiedView
                  ? 'border-indigo-400/70 dark:border-indigo-500/40 bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-200 hover:bg-indigo-200 dark:hover:bg-indigo-500/30'
                  : 'border-slate-200/70 dark:border-slate-700/50 bg-white dark:bg-slate-800/40 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50'
              }`}
            >
              <span aria-hidden="true" className="text-base leading-none">
                {isSimplifiedView ? '⊞' : '☰'}
              </span>
              {isSimplifiedView ? 'Full View' : 'Simple View'}
            </button>
          </div>
        </header>

        {/* Controls bar — search + filter, same pill container as Schedules */}
        <div className="mb-2 flex flex-wrap items-center gap-3 rounded-xl border border-slate-200/80 dark:border-slate-800/60 bg-white/70 dark:bg-slate-900/40 px-3 py-1.5 shadow-sm backdrop-blur-sm">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => onSearchInput(e.target.value)}
            placeholder="Search patient name, EHR ID, or email…"
            aria-label="Search patients by name, EHR ID, or email"
            className="flex-1 min-w-[220px] h-8 rounded-md border border-slate-200/70 dark:border-slate-700/50 bg-white dark:bg-slate-800/40 px-3 text-sm text-slate-700 dark:text-slate-200 placeholder:text-slate-400 shadow-sm"
          />
          <label className="inline-flex items-center gap-2 h-8 px-3 rounded-md border border-slate-200/70 dark:border-slate-700/50 bg-white dark:bg-slate-800/40 text-sm font-semibold text-slate-600 dark:text-slate-300 shadow-sm cursor-pointer select-none hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors">
            <input
              type="checkbox"
              className="accent-indigo-600"
              checked={onlyWithoutAppointment}
              onChange={(e) => setOnlyWithoutAppointment(e.target.checked)}
            />
            Only without an appointment
          </label>
          <label className="inline-flex items-center gap-2 h-8 px-3 rounded-md border border-slate-200/70 dark:border-slate-700/50 bg-white dark:bg-slate-800/40 text-sm font-semibold text-slate-600 dark:text-slate-300 shadow-sm select-none">
            <span>Rows</span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              aria-label="Rows per page"
              className="bg-transparent text-sm font-bold text-slate-700 dark:text-slate-200 outline-none cursor-pointer"
            >
              {/* Options are left unstyled on purpose: the popup is drawn by
                  the browser, and forcing a text colour here goes dark-on-dark
                  in a dark-mode popup. */}
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>
        </div>

        {/* The roster is bigger than one fetch can hold. Said out loud, because
            the alternative is a page that looks complete and isn't. */}
        {truncatedSchemas.length > 0 && (
          <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/40 text-amber-800 dark:text-amber-200 px-3 py-2 text-sm">
            Showing only the first {FETCH_CAP.toLocaleString()} patients
            {truncatedSchemas.length === 1 ? ' of ' : ' from each of '}
            {truncatedSchemas.map((s) => `“${s}”`).join(', ')}. Search to reach the rest.
          </div>
        )}

        {loadError && (
          <div className="mb-3 rounded-lg border border-red-300 bg-red-50 text-red-700 px-3 py-2 text-sm flex items-center justify-between">
            <span>{loadError}</span>
            <button type="button" className="underline" onClick={fetchPage}>Retry</button>
          </div>
        )}

        {/* Card — same wrapper the Schedules page puts around its table */}
        <div className="rounded-xl border border-slate-200 dark:border-slate-800/60 bg-white dark:bg-[#0a0a0f] shadow-sm overflow-hidden mb-3">
          {isSimplifiedView ? (
            isLoading ? (
              <p className="p-6 text-sm text-slate-400 text-center">Loading…</p>
            ) : (
              <LeejoeSchedulesView
                schedules={displayRows}
                userRole={userRole}
                homeSchema={homeSchema}
                onPatientClick={onPatientClick}
                title="Patient roster"
                unitLabel="patient"
                stats={rosterStats}
              />
            )
          ) : (
            <RosterTable rows={displayRows} onPatientClick={onPatientClick} isLoading={isLoading} userRole={userRole} homeSchema={homeSchema} />
          )}
        </div>

        {/* Pagination — buttons in the Schedules control style. pr-56 keeps
            the Next button clear of the app's fixed bottom-right bubble
            cluster (~215px wide) even when the page content is shorter than
            the viewport. */}
        <div className="flex items-center justify-between text-sm pr-56">
          <button
            type="button"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-200/70 dark:border-slate-700/50 bg-white dark:bg-slate-800/40 px-3 h-8 text-sm font-bold tracking-tight text-slate-600 dark:text-slate-300 shadow-sm hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            ← Prev
          </button>
          <span className="text-slate-500 dark:text-slate-400">
            Page {page + 1} of {totalPages} · {totalCount} patient{totalCount === 1 ? '' : 's'}
          </span>
          <button
            type="button"
            disabled={page + 1 >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-200/70 dark:border-slate-700/50 bg-white dark:bg-slate-800/40 px-3 h-8 text-sm font-bold tracking-tight text-slate-600 dark:text-slate-300 shadow-sm hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Next →
          </button>
        </div>
      </div>

      {/* Chooser — visited patients: patient chart or latest visit */}
      {chooserRow && (
        <div
          className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center"
          onClick={() => setChooserRow(null)}
        >
          <div
            className="bg-white dark:bg-[#0a0a0f] rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 p-5 w-80"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="font-semibold mb-1">{chooserRow.patient_name || '(no name)'}</p>
            <p className="text-xs text-slate-500 mb-4">
              This patient has {chooserRow._visitCount} scheduled visit{chooserRow._visitCount === 1 ? '' : 's'}.
            </p>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                className="rounded-lg bg-blue-600 text-white px-3 py-2 text-sm"
                onClick={() => { const r = chooserRow; setChooserRow(null); openPatientChart(r); }}
              >
                Patient chart
              </button>
              <button
                type="button"
                className="rounded-lg border border-slate-300 dark:border-slate-700 px-3 py-2 text-sm"
                onClick={() => { const r = chooserRow; setChooserRow(null); openLatestVisit(r); }}
              >
                Latest visit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
